import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, MenuItem, IconButton } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import PropTypes from 'prop-types';
import AdminPage from '../Components/AdminPage';
import FormSection from '../Components/FormSection';
import FileDrop from '../Components/FileDrop';
import AdminActionBar from '../Components/AdminActionBar';
import GradientCircularProgress from '../Components/GradientCircularProgress';
import isValidVersion from '../Components/versionValidation';

const APP_TYPES = ['Driver', 'BackSeat', 'Booking'];

const Upload = (props) => {
  const navigate = useNavigate();
  const updateRelink = () => {
    navigate('/update');
  };

  let compTemp = props.comps['driverApps'];
  let compTemp2 = compTemp.concat(props.comps['backSeatApps']);
  let compsTemp = compTemp2.concat(props.comps['bookingApps']);
  let compsNew = [];
  let ids = {};

  compsTemp.forEach((obj) => {
    if (!ids[obj.name]) {
      ids[obj.name] = true;
      compsNew.push(obj);
    }
  });

  const [existingAffiliate, setExistingAffiliate] = useState('');
  const [disabler, setDisabler] = useState(false);
  const [disabler2, setDisabler2] = useState(false);
  const [iAffiliateID, setIAffiliateID] = useState('');
  const [affiliateName, setAffiliateName] = useState('');
  const [nameNeedsConfirmation, setNameNeedsConfirmation] = useState(false);
  const [appType, setAppType] = useState('');
  const [versionNo, setVersionNo] = useState('');
  const [versionInfo, setVersionInfo] = useState('');
  const [apk, setApk] = useState(null);
  const [bgPhoto, setBgPhoto] = useState(null);
  const [appLogo, setAppLogo] = useState(null);
  const [aboutApp, setAboutApp] = useState('');
  const [screenshots, setScreenshots] = useState([]);
  const [isTesting, setIsTesting] = useState(false);
  const [appleLink, setAppleLink] = useState('');
  const [googleLink, setGoogleLink] = useState('');
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  /*
   * Unchanged from the pre-redesign page, deliberately. Upload.requests.test.js
   * pins every parameter, key and method below; if this function needs editing
   * to make the new markup work, the markup is wrong.
   */
  async function handleUpload(e) {
    e.preventDefault();
    const APILinksFetch = await fetch('/APILinks.json');
    const APILinks = await APILinksFetch.json();
    const tzoffset = new Date().getTimezoneOffset() * 60000; //offset in milliseconds
    const today = new Date(Date.now() - tzoffset).toISOString().slice(0, -1);

    let navLink = '/'.concat(affiliateName);
    navLink = navLink.concat(appType);

    let bgPath = '';

    let appLogoPath = '';

    let downloadLinkPath = '/pages/appPages/'.concat(appType);
    downloadLinkPath = downloadLinkPath.concat(
      `/${affiliateName}/${affiliateName}-${appType}-${versionNo.trim()}.apk`
    );

    if (!isTesting) {
      let data = new FormData();
      if (bgPhoto != null) {
        data.append('BGPhoto', bgPhoto);
        bgPath = `/pages/appPages/${appType}/${affiliateName}/BGPhoto.png`;
      }
      if (appLogo != null) {
        data.append('appLogo', appLogo);
        appLogoPath = `/pages/appPages/${appType}/${affiliateName}/logo.png`;
      }

      data.append('apk', apk);

      if (screenshots.length !== 0) {
        for (const file of screenshots) {
          data.append('screenshots', file);
        }
      }

      setIsLoading(true);
      const response = await fetch(
        APILinks['uploadFile'] +
          new URLSearchParams({
            iAffiliateID: Number(iAffiliateID),
            affiliateName: affiliateName,
            appType: appType,
            versionNo: versionNo.trim(),
            versionInfo: versionInfo,
            releaseDate: today,
            navLink: navLink,
            downloadLink: downloadLinkPath,
            BGPhoto: bgPath,
            appLogo: appLogoPath,
            isTesting: isTesting,
            aboutApp: aboutApp,
            appleLink: appleLink,
            googleLink: googleLink,
          }).toString(),
        {
          method: 'PUT',
          body: data,
        }
      );
      const uploadData = await response.json();
      if (uploadData['message'] === 'Completed') {
        setUploadComplete(true);
      } else {
        setUploadErrorMessage(uploadData['message']);
        setUploadError(true);
      }
    }

    if (isTesting) {
      let data = new FormData();
      data.append('apk', apk);
      setIsLoading(true);
      const response = await fetch(
        APILinks['uploadFile'] +
          new URLSearchParams({
            iAffiliateID: Number(iAffiliateID),
            affiliateName: affiliateName,
            appType: appType,
            versionNo: versionNo.trim(),
            versionInfo: 'x',
            releaseDate: today,
            navLink: 'x',
            downloadLink: downloadLinkPath,
            BGPhoto: 'x',
            appLogo: 'x',
            isTesting: isTesting,
            aboutApp: 'x',
            appleLink: 'x',
            googleLink: 'x',
          }).toString(),
        {
          method: 'PUT',
          body: data,
        }
      );

      const uploadData = await response.json();
      if (uploadData['message'] === 'Completed') {
        setUploadComplete(true);
      } else {
        setUploadErrorMessage(uploadData['message']);
        setUploadError(true);
      }
    }
  }

  function handleUploadClose() {
    setUploadComplete(false);
    setIsLoading(false);
  }

  function handleErrorClose() {
    setUploadError(false);
    setIsLoading(false);
  }

  function handleExistingAffiliate(e, selectedOption, count) {
    let typeList = [];
    compsTemp.forEach((app) => {
      if (app.name === e) {
        typeList.push(app);
      }
    });
    setExistingAffiliate(e);
    setAffiliateName(e);
    setIAffiliateID(typeList[0].iAffiliateID);
    setDisabler(true);
    setDisabler2(true);
    setNameNeedsConfirmation(false);
    if (count >= 1) {
      setAppType(selectedOption.appType);
    } else {
      setAppType('');
    }
  }

  /**
   * Unlocking Affiliate ID breaks the matched pair the autofill produced, so
   * if Affiliate Name is still in its autofilled/locked state, it can no
   * longer be trusted as-is. Force it open and flag it rather than leaving a
   * stale name silently paired with a newly edited ID.
   */
  function unlockAffiliateID() {
    setDisabler(false);
    if (disabler2) {
      setDisabler2(false);
      setNameNeedsConfirmation(true);
    }
  }

  /** What the action bar names when the submit button is still held back. */
  const requirements = [
    { label: 'Affiliate ID', done: iAffiliateID !== '' },
    { label: 'Affiliate Name', done: affiliateName !== '' && !nameNeedsConfirmation },
    { label: 'App Type', done: appType !== '' },
    { label: 'Version #', done: isValidVersion(versionNo) },
    { label: 'Version Changes', done: versionInfo !== '' },
    { label: 'APK', done: apk !== null },
  ];

  if (uploadComplete) {
    return (
      <AdminPage title="Upload a new APK">
        <div className="itcFormBanner itcFormBannerSuccess" role="status">
          <p className="itcFormBannerText">Upload has been completed</p>
          <button
            type="button"
            className="itcFormBannerDismiss"
            onClick={handleUploadClose}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      </AdminPage>
    );
  }

  let status = null;
  if (uploadError) {
    status = (
      <div className="itcFormBanner itcFormBannerError" role="alert">
        <p className="itcFormBannerText">{uploadErrorMessage}</p>
        <button
          type="button"
          className="itcFormBannerDismiss"
          onClick={handleErrorClose}
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    );
  } else if (isLoading) {
    status = <GradientCircularProgress />;
  }

  /** The pencil that unlocks a field the affiliate autofill filled in. */
  const unlock = (locked, unlockField) => ({
    input: {
      readOnly: locked,
      endAdornment: locked ? (
        <IconButton
          aria-label="Edit"
          color="inherit"
          size="small"
          onClick={unlockField}
        >
          <EditOutlinedIcon fontSize="inherit" />
        </IconButton>
      ) : undefined,
    },
  });

  /**
   * Affiliate Name has a third state beyond unlock()'s two: once the operator
   * edits the autofilled Affiliate ID, the paired Name can no longer be
   * trusted, so it must be confirmed even if left unchanged.
   */
  const affiliateNameSlotProps = disabler2
    ? unlock(true, () => setDisabler2(false))
    : nameNeedsConfirmation
    ? {
        input: {
          endAdornment: (
            <IconButton
              aria-label="Keep this name"
              color="inherit"
              size="small"
              onClick={() => setNameNeedsConfirmation(false)}
            >
              <CheckOutlinedIcon fontSize="inherit" />
            </IconButton>
          ),
        },
      }
    : undefined;

  return (
    <AdminPage title="Upload a new APK">
      <form autoComplete="off" onSubmit={handleUpload}>
        <div className="itcFormCard">
          <FormSection
            index={1}
            title="Affiliate"
            description="Pick an existing affiliate to autofill their ID and name, or type both in."
          >
            <TextField
              className="itcFieldFull"
              name="affiliate"
              select
              value={existingAffiliate}
              label="Affiliate"
              helperText="Autofill existing affiliate"
              onChange={(event) => {
                const selectedOption = compsNew.find(
                  (option) => option.name === event.target.value
                );
                const count = compsNew.filter(
                  (item) =>
                    item.iAffiliateID === selectedOption.iAffiliateID &&
                    item.name !== selectedOption.name
                ).length;
                handleExistingAffiliate(event.target.value, selectedOption, count);
              }}
            >
              {compsNew.map((option) => {
                const count = compsNew.filter(
                  (item) =>
                    item.iAffiliateID === option.iAffiliateID && item.name !== option.name
                ).length;
                return (
                  <MenuItem key={option.name} value={option.name}>
                    {count >= 1
                      ? `${option.name} (affID: ${option.iAffiliateID} ${option.appType})`
                      : `${option.name}`}
                  </MenuItem>
                );
              })}
            </TextField>

            <TextField
              className="itcFieldHalf"
              label="Affiliate ID"
              required
              value={iAffiliateID}
              slotProps={unlock(disabler, unlockAffiliateID)}
              onChange={(event) => setIAffiliateID(event.target.value)}
            />

            <TextField
              className="itcFieldHalf"
              name="affiliateName"
              label="Affiliate Name"
              required
              value={affiliateName}
              error={nameNeedsConfirmation}
              helperText={
                nameNeedsConfirmation
                  ? 'Confirm this still matches the new Affiliate ID'
                  : undefined
              }
              slotProps={affiliateNameSlotProps}
              onChange={(event) => {
                setAffiliateName(event.target.value);
                setNameNeedsConfirmation(false);
              }}
            />
          </FormSection>

          <FormSection index={2} title="Release" description="What is shipping in this build.">
            <TextField
              className="itcFieldHalf"
              name="appType"
              select
              required
              value={appType}
              label="App Type"
              onChange={(event) => setAppType(event.target.value)}
            >
              {APP_TYPES.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              className="itcFieldHalf"
              name="testing"
              select
              value={isTesting}
              label="Is Beta?"
              helperText="A beta build publishes the APK only — no listing."
              onChange={(event) => setIsTesting(event.target.value)}
            >
              <MenuItem key={'false'} value={false}>
                False
              </MenuItem>
              <MenuItem key={'true'} value={true}>
                True
              </MenuItem>
            </TextField>

            <TextField
              className="itcFieldHalf"
              label="Version #"
              required
              value={versionNo}
              error={versionNo !== '' && !isValidVersion(versionNo)}
              helperText={
                versionNo !== '' && !isValidVersion(versionNo)
                  ? 'Use a format like 1.2.3 or 1.2.3-beta'
                  : ''
              }
              onChange={(event) => setVersionNo(event.target.value)}
            />

            <TextField
              className="itcFieldFull"
              name="versionInfo"
              label="Version Changes"
              required
              fullWidth
              multiline
              minRows={3}
              value={versionInfo}
              onChange={(event) => setVersionInfo(event.target.value)}
            />
          </FormSection>

          <FormSection
            index={3}
            title="Files"
            description={
              isTesting
                ? 'A beta build ships the APK alone.'
                : 'The APK, plus the artwork the store listing shows.'
            }
          >
            <div className="itcFieldFull">
              <FileDrop
                label="Upload APK"
                accept=".apk"
                required
                value={apk}
                onChange={setApk}
                hint="A single .apk file"
              />
            </div>

            {!isTesting && (
              <div className="itcFieldHalf">
                <FileDrop
                  label="Background Photo"
                  accept="image/*"
                  value={bgPhoto}
                  onChange={setBgPhoto}
                  hint="PNG or JPG"
                />
              </div>
            )}

            {!isTesting && (
              <div className="itcFieldHalf">
                <FileDrop
                  label="App Logo"
                  accept="image/*"
                  value={appLogo}
                  onChange={setAppLogo}
                  hint="PNG or JPG"
                />
              </div>
            )}

            {!isTesting && (
              <div className="itcFieldFull">
                <FileDrop
                  label="Screenshots"
                  accept="image/*"
                  multiple
                  value={screenshots}
                  onChange={setScreenshots}
                  hint="Add as many as you like"
                />
              </div>
            )}
          </FormSection>

          {!isTesting && (
            <FormSection
              index={4}
              title="Store listing"
              description="What visitors read on the app's page."
            >
              <TextField
                className="itcFieldFull"
                name="aboutApp"
                label="About this app"
                fullWidth
                multiline
                minRows={3}
                value={aboutApp}
                onChange={(event) => setAboutApp(event.target.value)}
              />

              <TextField
                className="itcFieldHalf"
                name="appleLink"
                label="Apple Store Link"
                value={appleLink}
                onChange={(event) => setAppleLink(event.target.value)}
              />

              <TextField
                className="itcFieldHalf"
                name="googleLink"
                label="Play Store Link"
                value={googleLink}
                onChange={(event) => setGoogleLink(event.target.value)}
              />
            </FormSection>
          )}
        </div>

        <AdminActionBar
          requirements={requirements}
          submitLabel="Upload"
          status={status}
          busy={isLoading}
        />

        <p className="itcFormAside">
          <button type="button" className="itcFormAsideLink" onClick={updateRelink}>
            Want to update an existing APK listing? →
          </button>
        </p>
      </form>
    </AdminPage>
  );
};

export default Upload;

Upload.propTypes = {
  comps: PropTypes.any,
};
